import type {
  BackupAdapterKind,
  PlatformCapabilities,
  PlatformVersion,
  PurchaseProviderId,
} from './types.js'
import matrix from '../platforms.json' with { type: 'json' }

/**
 * Capability detection and the versioned platform matrix — PRD §10.1, D07.
 * The matrix is frozen before platform-specific flows; detection maps the host
 * onto one entry and can only *reduce* capabilities, never add unsupported ones.
 */

export interface PlatformMatrixEntry {
  readonly id: string
  readonly platform_id: string
  readonly os: readonly string[]
  readonly browser: readonly string[]
  readonly storage: PlatformCapabilities['storage_adapter']
  readonly secure_key: PlatformCapabilities['secure_key_adapter']
  readonly share_url: boolean
  readonly share_files: boolean
  readonly purchase_provider: PurchaseProviderId
  readonly decoders: readonly string[]
  readonly backup_adapters: readonly BackupAdapterKind[]
  readonly camera: boolean
  readonly contact_picker: boolean
  readonly local_ai_adapter: string | null
  readonly fallbacks: readonly string[]
}

export interface PlatformMatrix {
  readonly matrix_version: string
  readonly status: string
  readonly validation_status: string
  readonly source: string
  readonly entries: readonly PlatformMatrixEntry[]
}

export const PLATFORM_MATRIX = matrix as PlatformMatrix
export const PLATFORM_MATRIX_VERSION = PLATFORM_MATRIX.matrix_version

const DECODER_MIME: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpeg: 'image/jpeg',
  heic: 'image/heic',
}

export function supportedInputTypes(entry: PlatformMatrixEntry): string[] {
  return entry.decoders.map((decoder) => DECODER_MIME[decoder] ?? decoder)
}

export function findMatrixEntry(entryId: string): PlatformMatrixEntry | undefined {
  return PLATFORM_MATRIX.entries.find((entry) => entry.id === entryId)
}

export interface UserAgentInfo {
  readonly os: PlatformVersion
  readonly browser: PlatformVersion
  readonly in_app: boolean
  readonly form_factor: 'phone' | 'tablet' | 'desktop' | 'unknown'
}

const IN_APP_MARKERS = ['FBAN', 'FBAV', 'Instagram', 'Line/', 'Twitter', 'LinkedInApp', 'GSA/']

export function parseUserAgent(userAgent: string): UserAgentInfo {
  const inApp = IN_APP_MARKERS.some((marker) => userAgent.includes(marker))

  let os: PlatformVersion = { name: 'unknown', version: null }
  if (/Windows NT ([\d.]+)/.exec(userAgent) !== null) {
    os = { name: 'windows', version: /Windows NT ([\d.]+)/.exec(userAgent)?.[1] ?? null }
  } else if (/iPhone|iPod/.test(userAgent)) {
    os = {
      name: 'ios',
      version: /OS ([\d_]+) like Mac OS X/.exec(userAgent)?.[1]?.replace(/_/g, '.') ?? null,
    }
  } else if (/iPad/.test(userAgent)) {
    os = {
      name: 'ios',
      version: /OS ([\d_]+) like Mac OS X/.exec(userAgent)?.[1]?.replace(/_/g, '.') ?? null,
    }
  } else if (/Android ([\d.]+)/.exec(userAgent) !== null) {
    os = { name: 'android', version: /Android ([\d.]+)/.exec(userAgent)?.[1] ?? null }
  } else if (/Mac OS X ([\d_]+)/.exec(userAgent) !== null) {
    os = {
      name: 'macos',
      version: /Mac OS X ([\d_]+)/.exec(userAgent)?.[1]?.replace(/_/g, '.') ?? null,
    }
  } else if (/Linux/.test(userAgent)) {
    os = { name: 'linux', version: null }
  }

  let browser: PlatformVersion = { name: 'unknown', version: null }
  if (/Edg\/([\d.]+)/.exec(userAgent) !== null) {
    browser = { name: 'edge', version: /Edg\/([\d.]+)/.exec(userAgent)?.[1] ?? null }
  } else if (/Firefox\/([\d.]+)/.exec(userAgent) !== null) {
    browser = { name: 'firefox', version: /Firefox\/([\d.]+)/.exec(userAgent)?.[1] ?? null }
  } else if (/Chrome\/([\d.]+)/.exec(userAgent) !== null) {
    browser = { name: 'chrome', version: /Chrome\/([\d.]+)/.exec(userAgent)?.[1] ?? null }
  } else if (/Version\/([\d.]+).*Safari/.exec(userAgent) !== null) {
    browser = { name: 'safari', version: /Version\/([\d.]+).*Safari/.exec(userAgent)?.[1] ?? null }
  }

  if (inApp) browser = { name: 'in-app', version: browser.version }

  let formFactor: UserAgentInfo['form_factor'] = 'desktop'
  if (/iPad/.test(userAgent)) formFactor = 'tablet'
  else if (/iPhone|iPod/.test(userAgent)) formFactor = 'phone'
  else if (os.name === 'android') formFactor = /Mobile/.test(userAgent) ? 'phone' : 'tablet'
  else if (os.name === 'unknown') formFactor = 'unknown'

  return { os, browser, in_app: inApp, form_factor: formFactor }
}

export function resolveMatrixEntry(info: UserAgentInfo): PlatformMatrixEntry {
  const fallback = findMatrixEntry('web-unknown') as PlatformMatrixEntry
  if (info.in_app) return findMatrixEntry('in-app-browser') ?? fallback
  if (info.browser.name === 'native') return findMatrixEntry(`native-${info.os.name}`) ?? fallback

  const candidates: string[] = []
  if (info.browser.name === 'chrome' || info.browser.name === 'edge') {
    if (info.os.name === 'android') candidates.push('android-chrome-pwa')
    candidates.push('desktop-chromium')
  }
  if (info.browser.name === 'firefox') candidates.push('desktop-firefox')
  if (info.browser.name === 'safari') {
    if (info.os.name === 'ios') {
      candidates.push(info.form_factor === 'tablet' ? 'ipad-safari-pwa' : 'ios-safari')
    } else if (info.os.name === 'macos') {
      candidates.push('macos-safari')
    }
  }
  return (
    candidates
      .map((id) => findMatrixEntry(id))
      .find((entry): entry is PlatformMatrixEntry => entry !== undefined) ?? fallback
  )
}

export interface WebCapabilityEnvironment {
  readonly user_agent?: string
  readonly app_build?: string
  readonly has_web_share?: boolean
  readonly has_opfs?: boolean
  readonly has_file_system_access?: boolean
  readonly has_contacts?: boolean
  readonly has_camera?: boolean
  readonly local_ai_adapter?: string | null
}

function detectUserAgent(): string {
  const navigatorLike = (globalThis as { navigator?: { userAgent?: string } }).navigator
  return navigatorLike?.userAgent ?? ''
}

export function detectWebCapabilities(env: WebCapabilityEnvironment = {}): PlatformCapabilities {
  const userAgent = env.user_agent ?? detectUserAgent()
  const info = parseUserAgent(userAgent)
  const entry = resolveMatrixEntry(info)

  const hasOpfs = env.has_opfs ?? entry.storage === 'indexeddb-opfs'
  const storage = entry.storage === 'indexeddb-opfs' && !hasOpfs ? 'indexeddb-only' : entry.storage

  const shareUrl = entry.share_url && (env.has_web_share ?? true)
  const shareFiles = entry.share_files && (env.has_web_share ?? true)

  const backupAdapters = entry.backup_adapters.filter((adapter) => {
    if (adapter === 'file_system_access') return env.has_file_system_access ?? true
    return true
  })

  return {
    platform_id: entry.platform_id,
    os: info.os,
    browser: info.browser,
    app_build: env.app_build ?? 'web-dev',
    storage_adapter: storage,
    secure_key_adapter: entry.secure_key,
    supported_input_types: supportedInputTypes(entry),
    share_url: shareUrl,
    share_files: shareFiles,
    share_requires_activation: shareUrl || shareFiles,
    purchase_provider: entry.purchase_provider,
    backup_adapters: backupAdapters.length > 0 ? backupAdapters : ['download'],
    camera: entry.camera && (env.has_camera ?? true),
    contact_picker: entry.contact_picker && (env.has_contacts ?? true),
    local_ai_adapter: env.local_ai_adapter ?? entry.local_ai_adapter,
    matrix_version: PLATFORM_MATRIX_VERSION,
    matrix_entry_id: entry.id,
  }
}
